import { take, filter, map, distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';


/**
 * Manages the global state and API interactions for a user's
 * Accounts, via the new ProviderAccounts API.
 */
export class ProviderAccountsService {

  _connectionSources = {};

  /** @type {BehaviorSubject<import('types/api').ProviderAccountFromAPI[]>} */
  _subject = new BehaviorSubject([]);

  /** @type {Observable<import('types/api').ProviderAccountFromAPI[]>} */
  providerAccounts$ =
    this._subject.pipe(
      map(_ => filterByConnectionStatus('ACTIVE', _)),
      distinctUntilChanged(angular.equals),
    );

  /** @type {Observable<ArchivedProviderAccount[]>} */
  providerAccountsWithArchives$ =
    this._subject.pipe(
      map(_ => filterByConnectionStatus('ARCHIVED', _)),
      // fill in connection source info,
      distinctUntilChanged(angular.equals),
    );

  // this.pollDelayInMs = MINIMUM_DELAY_IN_MS;
  _upcomingHandle = null;

  /**
   * Fetches provider accounts from the API and updates the service's
   * data. If data changes, events are fired.
   *
   * @return {ng.IPromise<unknown>}
   */
  fetchProviderAccounts() {
    return this._ProviderAccountResource.fetchAll()
      .$promise
      .then(providerAccountResults => {
        this._subject.next(providerAccountResults.items);
      });
  }

  /**
   * return {void}
   */
  pollAccounts() {
    if (this._upcomingHandle == null) {
      this._upcomingHandle =
        this._$interval(
          () => {
            this.fetchProviderAccounts();
          },
          POLL_INTERVAL_IN_MS,
        );
    }
  }

  /**
   * @param {ProviderAccountsService.AccountFromAPI} entity
   * @param {string} newValue
   * @return {ng.IPromise<unknown>}
   */
  accountWasRenamed(entity, newValue) {
    var value = newValue;
    if (value == "") { value = null };

    return this._$q.resolve(this._AccountResource
      .update({id: entity.id}, {friendlyName: value})
      .$promise)
      .then(() => {
        return this.fetchProviderAccounts();
      });
  }

  /**
   * @param {string} accountId
   * @return {ng.IPromise<unknown>}
   */
  removeAccount(accountId) {
    return this._AccountResource.delete({id: accountId}).$promise
      .then(result => {
        return this.fetchProviderAccounts()
          .then(() => result);
      });
  }



  _FEATURES;
  _UserService;
  _AccountResource;
  /** @type {import('api-services/provider-account-resource.factory').IProviderAccountResource} */
  _ProviderAccountResource;
  _ConnectionSourceResource;
  _$interval;
  _$q;

  constructor (
    UserService,
    AccountResource,
    ProviderAccountResource,
    ConnectionSourceResource,
    FEATURES,
    $interval,
    $q,
  ) {
    this._UserService = UserService;
    this._AccountResource = AccountResource;
    this._ProviderAccountResource = ProviderAccountResource;
    this._ConnectionSourceResource = ConnectionSourceResource;
    this._FEATURES = FEATURES;
    this._$interval = $interval;
    this._$q = $q;

    UserService.isAuthenticated$
      .pipe(
        filter(_ => _ == true),
        take(1),
      ).subscribe(user => {
        return this.fetchProviderAccounts();
      });
  }
}

export default [
  'UserService',
  'AccountResource',
  'ProviderAccountResource',
  'ConnectionSourceResource',
  'FEATURES',
  '$interval',
  '$q',
  ProviderAccountsService,
];

const POLL_INTERVAL_IN_MS = 15000;

/**
 * @param {import("types/api").ProviderAccountFromAPI[]} providerAccounts
 * @param {'ACTIVE' | 'ARCHIVED'} status
 */
function filterByConnectionStatus(
  status,
  providerAccounts,
) {
  const results = providerAccounts
    .map(providerAccount => {
      const providerAccountCopy =
        angular.copy(providerAccount);

      const accounts = providerAccountCopy.accounts || [];
      delete providerAccountCopy.accounts;

      angular.merge(providerAccountCopy, {
        accounts: accounts
          .filter(account => account.connectionInfo.status == status),
      });

      return providerAccountCopy;
    })
    .filter(providerAccount =>
      providerAccount.accounts.length > 0
    );

  return results;
}


/**
 * @typedef {import('types/api').ProviderAccountFromAPI} ProviderAccountsService.ProviderAccountFromAPI
 * @typedef {import('types/api').AccountFromAPI} ProviderAccountsService.AccountFromAPI
 */

 /**
  * @typedef { ProviderAccountsService.ProviderAccountFromAPI
  *            & { availableSources?: string[] } } ArchivedProviderAccount
  */

 /**
  * @typedef {object} ProviderAccountsService.ChangeListener
  * @property {'accounts'} eventName;
  * @property {(args: { providerAccounts: ProviderAccountsService.ProviderAccountFromAPI[] }) => void} handler
  */